import { FormModalBaseComponent } from '../../../../../../core/base/components/form-modal-base/form-modal-base.component';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { EstablishmentService } from '../../establishment.service';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { BuildingModel } from '../../../../../../core/models/building.model';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BuildingService } from '../../../building/building.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import icAdd from '@iconify/icons-ic/twotone-add';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { BuildingCreateUpdateDeleteComponent } from '../../../building/modals/building-create-update-delete/building-create-update-delete.component';
import { Router } from '@angular/router';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';

@Component({
  selector: 'app-establishment-building-create-update',
  templateUrl: './establishment-building-create-update.component.html',
  styleUrls: ['./establishment-building-create-update.component.scss'],
})
export class EstablishmentBuildingCreateUpdateComponent
  extends FormModalBaseComponent<EstablishmentModel>
  implements OnInit, OnDestroy
{
  buildings$: Observable<BuildingModel[]>;
  establishment?: EstablishmentModel = this.data.defaults;
  readonly icAdd = icAdd;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { defaults: EstablishmentModel; mode: CrudModeEnum },
    public service: EstablishmentService,
    public dialogRef: MatDialogRef<FormModalBaseComponent<EstablishmentModel>>,
    public buildingService: BuildingService,
    private dialog: MatDialog,
    private router: Router,
    public snackbarService: SnackbarService,
  ) {
    super(data, dialogRef, service);
  }

  ngOnInit() {
    super.ngOnInit();

    this.autocompleteBuilding();

    this.form = new UntypedFormGroup({
      building: new UntypedFormControl(null, [Validators.required]),
      surface: new UntypedFormControl(null, [Validators.required]),
    });

    this.form.controls.building.valueChanges.subscribe(building => {
      if (!building) {
        this.autocompleteBuilding('');
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  autocompleteBuilding(search: string = '') {
    // Récupérer l'établissement principal et son label
    const mainEstablishmentId = this.data.defaults?.mainEstablishmentId;
    const mainEstablishmentLabel = this.data.defaults?.mainEstablishmentData?.label;

    if (!mainEstablishmentId) {
      // Si pas d'établissement principal, afficher uniquement les bâtiments avec surface disponible
      this.buildings$ = this.buildingService
        .findAll(null, null, 'label', 'asc', search, {
          availableSurfacesOnly: true,
          labelSurfaceNeeded: true,
          requiredFields: ['labelSurface', 'availableSurface'],
        })
        .pipe(map(x => x.data));
      return;
    }

    // Charger les bâtiments en deux requêtes parallèles :
    // 1. Bâtiments de l'établissement principal (tous, même sans surface)
    // 2. Bâtiments avec surface disponible (tous les autres)
    const mainEstablishmentBuildings$ = this.buildingService
      .findAll(null, null, 'label', 'asc', search, {
        establishmentId: mainEstablishmentId,
        labelSurfaceNeeded: true,
        requiredFields: ['labelSurface', 'availableSurface', 'establishments'],
      })
      .pipe(map(response => response.data));

    const availableBuildings$ = this.buildingService
      .findAll(null, null, 'label', 'asc', search, {
        availableSurfacesOnly: true,
        labelSurfaceNeeded: true,
        requiredFields: ['labelSurface', 'availableSurface', 'establishments'],
      })
      .pipe(map(response => response.data));

    // Fusionner les deux listes et dédupliquer
    this.buildings$ = new Observable(observer => {
      Promise.all([mainEstablishmentBuildings$.toPromise(), availableBuildings$.toPromise()])
        .then(([mainBuildings, availableBuildings]) => {
          // Marquer les bâtiments de l'établissement principal
          const mainBuildingsMarked = mainBuildings.map((building: any) => ({
            ...building,
            labelSurface: `${building.labelSurface} (${mainEstablishmentLabel})`,
            isFromMainEstablishment: true,
          }));

          // Créer un Set des IDs des bâtiments principaux pour éviter les doublons
          const mainBuildingIds = new Set(mainBuildings.map((b: any) => b._id));

          // Filtrer les bâtiments disponibles pour exclure ceux déjà dans l'établissement principal
          const otherBuildings = availableBuildings.filter((building: any) => !mainBuildingIds.has(building._id));

          // Combiner les deux listes
          const allBuildings = [...mainBuildingsMarked, ...otherBuildings];

          observer.next(allBuildings);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  createBuilding(): void {
    this.dialog
      .open(BuildingCreateUpdateDeleteComponent, {
        data: {
          defaults: null,
          establishment: this.data.defaults,
          mode: CrudModeEnum.Create,
        },
        width: '600px',
      })
      .afterClosed()
      .subscribe((building: BuildingModel) => {
        if (building && building._id) {
          // If a building was created, it is automatically linked so we close also this modal.
          this.dialogRef.close(building);
          this.snackbarService.warning(
            `Compléter les autres paramètres de ${building.label} accessible dans le menu Bâtiment`,
          );
        }
      });
  }

  createItem() {
    if (this.form.valid) {
      const establishmentId = this.data.defaults._id;
      const building = this.form.controls.building.value;
      const surface = this.form.controls.surface.value;

      if (
        establishmentId === null ||
        establishmentId === undefined ||
        building === null ||
        building === undefined ||
        surface === null ||
        surface === undefined
      ) {
        return;
      }

      if (surface > building.availableSurface) {
        this.form.controls.surface.setErrors({ max: true });
        return;
      }

      this.loading = true;
      this.buildingService
        .addEstablishment(building._id, establishmentId, surface)
        .pipe(take(1))
        .subscribe(
          result => {
            this.loading = false;
            this.snackbarService.success('Bâtiment ajouté avec succès');
            // Retourner true pour signaler le succès au parent
            this.dialogRef.close(true);
          },
          error => {
            this.loading = false;
            this.setErrorsMessages(error?.error?.errors);
          },
        );
    } else {
      this.form.markAllAsTouched();
    }
  }
}
